import React from 'react';
import * as RadixDialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';
import IconButton from './IconButton';
import { useTheme } from './theme/ThemeProvider';

interface DialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: React.ReactNode;
  /**
   * Action buttons, secondary first then primary (e.g. Cancel, Log out).
   * Desktop shows them in a right-aligned row; mobile stacks them full-width with the primary on top.
   */
  actions: React.ReactNode;
}

/**
 * Confirmation dialog for destructive or irreversible actions (Figma "Modal").
 * Bottom sheet on mobile, centred 440px dialog from tablet up. Built on Radix Dialog
 * for focus trapping, Esc to close and screen-reader semantics.
 */
const Dialog: React.FC<DialogProps> = ({ open, onOpenChange, title, description, actions }) => {
  const { portalContainer } = useTheme();
  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      <RadixDialog.Portal container={portalContainer}>
        <RadixDialog.Overlay className="tw-fixed tw-inset-0 tw-z-50 tw-bg-ds-scrim" />
        <RadixDialog.Content
          className={cn(
            'tw-fixed tw-z-50 tw-flex tw-flex-col tw-gap-4 tw-bg-ds-surface tw-shadow-ds-modal focus:tw-outline-none',
            // Mobile: bottom sheet
            'tw-inset-x-0 tw-bottom-0 tw-rounded-t-3xl tw-px-5 tw-pt-3 tw-pb-[calc(28px+env(safe-area-inset-bottom))]',
            // Tablet and up: centred dialog
            'md:tw-inset-x-auto md:tw-bottom-auto md:tw-left-1/2 md:tw-top-1/2 md:tw-w-[calc(100%-32px)] md:tw-max-w-[440px] md:-tw-translate-x-1/2 md:-tw-translate-y-1/2 md:tw-rounded-[20px] md:tw-p-6',
          )}
        >
          <span aria-hidden className="tw-mx-auto tw-h-1 tw-w-10 tw-rounded-sm tw-bg-ds-border-strong md:tw-hidden" />
          <div className="tw-flex tw-items-start tw-gap-3">
            <div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-1.5">
              <RadixDialog.Title className="tw-text-[20px] tw-font-semibold tw-leading-7 tw-text-ds-text-primary">
                {title}
              </RadixDialog.Title>
              {description && (
                <RadixDialog.Description className="tw-text-[14px] tw-leading-5 tw-text-ds-text-secondary">
                  {description}
                </RadixDialog.Description>
              )}
            </div>
            <RadixDialog.Close asChild>
              <IconButton icon={X} label="Close" className="tw-hidden md:tw-flex" />
            </RadixDialog.Close>
          </div>
          <div className="tw-flex tw-flex-col-reverse tw-gap-2.5 [&>*]:tw-w-full md:tw-flex-row md:tw-justify-end md:[&>*]:tw-w-auto">
            {actions}
          </div>
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
};

export default Dialog;
